import {
  call, put, select, takeEvery,
} from 'redux-saga/effects';
import { findMatch } from '@/utils/resultsHelper';
import callAdapter from '@/utils/sagaHelper';
import selectors from '@/selectors';
import { showCohortVisualisation, showCohortVisualisationError } from '../actions';
import SelectionVisualisationService from '../services/selectionVisualisationService';
import CohortVisualisationService from '../services/cohortVisualisationService';
import actions, { cohortChart } from '../constants';
import { chartInfo, visualisationActionWithDictionary } from '../helper';
import { findQuery } from '../../state/shared';
import { getCohortSourceProps } from '../../sagaHelper';
import SystemCohortVisualisationService from '../../system/services/systemCohortVisualisationService';
import cohortFilterActions from '../../filters/constants';
import cohortClauseActions from '../../clauses/constants';

function* getCohortVisualisationParams(action, chartType) {
  const items = yield select(selectors.cohorts);
  const { cohort, query } = findQuery(items, action);
  const { code, settings, requestCode } = cohort;
  const { uuid, id, activeDictionaryCode } = query;
  const chart = findMatch(cohort.visualisations, chartType);

  return {
    code,
    queryId: id,
    dictionaryCode: activeDictionaryCode,
    uuid,
    chart,
    allowQuery: !!settings?.expose_cohort_visualisations,
    requestCode,
  };
}

function* handleChartResponse({ category, code }, uuid, { response, error }, chartType) {
  if (error) {
    yield put(showCohortVisualisationError(category, code, chartType, uuid, error));
  } else {
    yield put(showCohortVisualisation(category, code, chartType, uuid, response));
  }
}

// Bar chart

function* fetchCohortVisualisationBarChart(action, isSystem) {
  const {
    chart, code, requestCode, queryId, dictionaryCode, uuid, allowQuery,
  } = yield getCohortVisualisationParams(action, cohortChart.BAR);

  if (!allowQuery) {
    return;
  }

  let service = CohortVisualisationService;
  let params = [chart, code, queryId, dictionaryCode];
  if (isSystem) {
    service = SystemCohortVisualisationService;
    params = [chart, requestCode, queryId, dictionaryCode];
  }

  const response = yield call(callAdapter, service.barChart(...params));
  yield handleChartResponse(action, uuid, response, cohortChart.BAR);
}

function* fetchSelectionVisualisationBarChart(action) {
  const items = yield select(selectors.cohorts);
  const { chart, clauses, uuid } = chartInfo(action, items, cohortChart.BAR);

  if (clauses) {
    const response = yield call(callAdapter, SelectionVisualisationService.barChart(chart, clauses));
    yield handleChartResponse(action, uuid, response, cohortChart.BAR);
  }
}

export function* fetchCohortBarChart(action) {
  const { isSystem, isEditing } = yield getCohortSourceProps(action);

  if (isEditing) {
    yield fetchSelectionVisualisationBarChart(action);
  } else {
    yield fetchCohortVisualisationBarChart(action, isSystem);
  }
}

// Stacked bar chart

function* fetchCohortVisualisationStackedBarChart(action, isSystem) {
  const {
    chart, code, requestCode, queryId, dictionaryCode, uuid, allowQuery,
  } = yield getCohortVisualisationParams(action, cohortChart.STACKED_BAR);

  if (!allowQuery) {
    return;
  }

  let service = CohortVisualisationService;
  let params = [chart, code, queryId, dictionaryCode];
  if (isSystem) {
    service = SystemCohortVisualisationService;
    params = [chart, requestCode, queryId, dictionaryCode];
  }

  const response = yield call(callAdapter, service.stackedBarChart(...params));
  yield handleChartResponse(action, uuid, response, cohortChart.STACKED_BAR);
}

function* fetchSelectionVisualisationStackedBarChar(action) {
  const items = yield select(selectors.cohorts);
  const { chart, clauses, uuid } = chartInfo(action, items, cohortChart.STACKED_BAR);

  if (clauses) {
    const response = yield call(callAdapter, SelectionVisualisationService.stackedBarChart(chart, clauses));
    yield handleChartResponse(action, uuid, response, cohortChart.STACKED_BAR);
  }
}

export function* fetchCohortStackedBarChart(action) {
  const { isSystem, isEditing } = yield getCohortSourceProps(action);

  if (isEditing) {
    yield fetchSelectionVisualisationStackedBarChar(action);
  } else {
    yield fetchCohortVisualisationStackedBarChart(action, isSystem);
  }
}

// Box plot

function* fetchCohortVisualisationBoxPlot(action, isSystem) {
  const {
    chart, code, requestCode, queryId, dictionaryCode, uuid, allowQuery,
  } = yield getCohortVisualisationParams(action, cohortChart.BOX_PLOT);

  if (!allowQuery) {
    return;
  }

  let service = CohortVisualisationService;
  let params = [chart, code, queryId, dictionaryCode];
  if (isSystem) {
    service = SystemCohortVisualisationService;
    params = [chart, requestCode, queryId, dictionaryCode];
  }

  const response = yield call(callAdapter, service.boxPlot(...params));
  yield handleChartResponse(action, uuid, response, cohortChart.BOX_PLOT);
}

function* fetchSelectionVisualisationBoxPlot(action) {
  const items = yield select(selectors.cohorts);
  const { chart, clauses, uuid } = chartInfo(action, items, cohortChart.BOX_PLOT);

  if (clauses) {
    const response = yield call(callAdapter, SelectionVisualisationService.boxPlot(chart, clauses));
    yield handleChartResponse(action, uuid, response, cohortChart.BOX_PLOT);
  }
}

export function* fetchCohortBoxPlot(action) {
  const { isSystem, isEditing } = yield getCohortSourceProps(action);

  if (isEditing) {
    yield fetchSelectionVisualisationBoxPlot(action);
  } else {
    yield fetchCohortVisualisationBoxPlot(action, isSystem);
  }
}

// Histogram

function* fetchCohortVisualisationHistogram(action, isSystem) {
  const {
    chart, code, requestCode, queryId, dictionaryCode, uuid, allowQuery,
  } = yield getCohortVisualisationParams(action, cohortChart.HISTOGRAM);

  if (!allowQuery) {
    return;
  }

  let service = CohortVisualisationService;
  let params = [chart, code, queryId, dictionaryCode];
  if (isSystem) {
    service = SystemCohortVisualisationService;
    params = [chart, requestCode, queryId, dictionaryCode];
  }

  const response = yield call(callAdapter, service.histogram(...params));
  yield handleChartResponse(action, uuid, response, cohortChart.HISTOGRAM);
}

function* fetchSelectionVisualisationHistogram(action) {
  const items = yield select(selectors.cohorts);
  const { chart, clauses, uuid } = chartInfo(action, items, cohortChart.HISTOGRAM);

  if (clauses) {
    const response = yield call(callAdapter, SelectionVisualisationService.histogram(chart, clauses));
    yield handleChartResponse(action, uuid, response, cohortChart.HISTOGRAM);
  }
}

export function* fetchCohortHistogram(action) {
  const { isSystem, isEditing } = yield getCohortSourceProps(action);

  if (isEditing) {
    yield fetchSelectionVisualisationHistogram(action);
  } else {
    yield fetchCohortVisualisationHistogram(action, isSystem);
  }
}

export function* fetchCohortVisualisation(action) {
  const items = yield select(selectors.cohorts);
  const actionWithDictionary = visualisationActionWithDictionary(action, items);

  yield call({
    [cohortChart.BAR]: fetchCohortBarChart,
    [cohortChart.BOX_PLOT]: fetchCohortBoxPlot,
    [cohortChart.HISTOGRAM]: fetchCohortHistogram,
    [cohortChart.STACKED_BAR]: fetchCohortStackedBarChart,
  }[actionWithDictionary.chartType], actionWithDictionary);
}

export default function* cohortVisualisationApi() {
  yield takeEvery(actions.FETCH_COHORT_VISUALISATION_BAR_QUERY, fetchCohortBarChart);
  yield takeEvery(actions.FETCH_COHORT_VISUALISATION_STACKED_BAR_QUERY, fetchCohortStackedBarChart);
  yield takeEvery(actions.FETCH_COHORT_VISUALISATION_BOX_PLOT_QUERY, fetchCohortBoxPlot);
  yield takeEvery(actions.FETCH_COHORT_VISUALISATION_HISTOGRAM_QUERY, fetchCohortHistogram);
  yield takeEvery(
    [
      actions.REFRESH_COHORT_VISUALISATION,
      cohortFilterActions.ADD_QUERY_FILTER,
      cohortClauseActions.REMOVE_COHORT_CLAUSE,
      cohortClauseActions.CLONE_COHORT_CLAUSE,
      cohortFilterActions.REMOVE_FILTER_FROM_COHORT,
    ],
    fetchCohortVisualisation,
  );
}
